// src/services/api.js
import axios from 'axios';
import { toast } from 'react-toastify';
import { handleMembershipLimitError } from '../utils/errorHandlers';
import * as mentorService from './mentorService';

// Import questionnaire API
import { questionnaireApi } from './questionnaireApi';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
});



// Helper to safely access localStorage
const getToken = () => {
  try {
    return localStorage.getItem('token');
  } catch (e) {
    return null;
  }
};

const getRefreshToken = () => {
  try {
    return getRefreshToken();
  } catch (e) {
    return null;
  }
};

const setToken = (token) => {
  try {
    localStorage.setItem('token', token);
  } catch (e) {
    // Ignore storage errors
  }
};

const removeTokens = () => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
  } catch (e) {
    // Ignore storage errors
  }
};

// Add a request interceptor to add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);


// Add a response interceptor for handling token expiration and membership limits
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If we get a 401 and haven't tried to refresh yet
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();

        if (refreshToken) {
          // Try to refresh the token
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/auth/refresh`,
            { refresh_token: refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
          );

          if (response.data.access_token) {
            // Update the stored token
            setToken(response.data.access_token);
            api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;

            // Retry the original request
            originalRequest.headers['Authorization'] = `Bearer ${response.data.access_token}`;
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        console.error('Token refresh failed:', refreshError);
      }
    }

    // If we get a 401 and it's not a public page, redirect to login
    if (error.response && error.response.status === 401) {
      removeTokens();
      const publicPaths = ['/', '/login', '/register', '/privacy-policy', '/terms', '/team'];
      const currentPath = window.location.pathname;

      if (!publicPaths.includes(currentPath) && currentPath !== '/login') {
        window.location.href = '/login';
      }
    } else if (error.response && error.response.status === 403) {
      // Check if it's a membership limit error
      const isMembershipError = handleMembershipLimitError(error);
      if (!isMembershipError) {
        // If not a membership error, show the default error message
        toast.error(error.response.data?.detail || 'Access denied');
      }
    }
    return Promise.reject(error);
  }
);

// Auth API endpoints
const authApi = {
  login: (email, password) => {
    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);

    return api.post('/api/auth/login', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
  },

  googleLogin: (credential) => api.post('/api/auth/google-login', { credential }),

  linkedinLogin: (code) => api.post('/api/auth/linkedin-login', { code }),

  register: (userData) => api.post('/api/auth/register', userData),

  registerWithGoogle: (userData) => api.post('/api/auth/register/google', userData),

  registerWithLinkedin: (userData) => api.post('/api/auth/register/linkedin', userData),

  getCurrentUser: () => api.get('/api/auth/me'),

  // Add the new profile update endpoint
  updateProfile: (profileData) => api.patch('/api/auth/profile', profileData),

  // Add the new password change endpoint
  changePassword: (passwordData) => api.post('/api/auth/change-password', passwordData),

  // src/services/api.js - Add to authApi object

// Password reset endpoints
  forgotPassword: (email) =>
    api.post('/api/auth/forgot-password', { email }),

  resetPassword: (email, token, new_password) =>
    api.post('/api/auth/reset-password', {
      email,
      token,
      new_password
    }),

  registerRequest: (userData) =>
    api.post('/api/auth/register/request', userData),

  verifyRegistration: (email, verificationCode, mentorCode = null) =>
    api.post('/api/auth/register/verify', {
      email,
      verification_code: verificationCode,
      mentor_code: mentorCode
    }),

  resendVerificationCode: (email) =>
    api.post('/api/auth/register/resend-code', { email }),
};


const ticketApi = {
  createTicket: (ticketData) => api.post('/api/tickets', ticketData),
  getUserTickets: () => api.get('/api/tickets'),
  getTicketById: (ticketId) => api.get(`/api/tickets/${ticketId}`),
  updateTicket: (ticketId, updateData) => api.patch(`/api/tickets/${ticketId}`, updateData)
};

// Resume API endpoints
const resumeApi = {
  getDashboardStats: () => api.get('/api/dashboard/stats'),

  uploadResume: (formData) => {
    return api.post('/api/resumes/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  createBlankResume: (data) => {
    return api.post('/api/resumes/create-blank', data);
  },

  uploadResumeText: (data) => {
    return api.post('/api/resumes/upload-text', data);
  },

  // Get a specific resume version
  getResumeVersion: (resumeId, versionId) =>
    api.get(`/api/resumes/${resumeId}/versions/${versionId}`),

  // Get the first version of a resume (original version)
  getFirstResumeVersion: (resumeId) =>
    api.get(`/api/resumes/${resumeId}/first_version`),
  // Update version content - this is what you should use when editing experience, education, etc.
  updateVersionContent: (resumeId, versionId, fields) =>
    api.patch(`/api/resumes/${resumeId}/versions/${versionId}`, { fields }),

  // Update resume metadata only - name, language, etc.
  updateResumeMetadata: (resumeId, updateData) =>
    api.patch(`/api/resumes/${resumeId}`, updateData),

  getAllResumes: (params) =>
    params == null ? api.get('/api/resumes') : api.get('/api/resumes', { params }),

  getResumeById: (resumeId) => api.get(`/api/resumes/${resumeId}`),

  updateResume: (resumeId, fields) => api.patch(`/api/resumes/${resumeId}`, { fields }),

  // In src/services/api.js
  updateResumeContent: (resumeId, fields) =>
    api.patch(`/api/resumes/${resumeId}/content`, { fields }),

// Update this in resumeApi
  polishResume: (resumeId, sections, jobDescription, versionName, additionalInfo = null, resumeContent = null, experienceIndexes = null, options = {}) =>
  api.post(`/api/resumes/${resumeId}/polish`, {
    sections,
    jobDescription,
    versionName,
    additionalInfo,
    resumeContent,
    experienceIndexes,
    cost: options.cost,
    removeSummary: options.removeSummary,
    sectionCustomInstructions: options.sectionCustomInstructions,
    experienceBulletLimits: options.experienceBulletLimits,
    experienceFilterMode: options.experienceFilterMode,
  }),

  generatePDF: (resumeId, versionId = null, resume_style = 'classic', file_format = 'pdf', options = {}) => {
    const body = { resumeId, versionId, resume_style, file_format };
    if (options.download_filename != null && String(options.download_filename).trim()) {
      body.download_filename = String(options.download_filename).trim();
    }
    return api.post('/api/pdf/generate', body)
      .then(response => {
        if (response.data && response.data.success && response.data.fileUrl) {
          return response;
        }
        toast.error('Failed to generate PDF. No download URL received.');
        return Promise.reject(new Error('No download URL received'));
      })
      .catch(error => {
        console.error('Error generating PDF:', error);
        toast.error('Failed to generate PDF');
        return Promise.reject(error);
      });
  },

  // Custom resume styles (user-defined style overrides)
  listCustomStyles: () => api.get('/api/resume-styles/custom'),
  createCustomStyle: (data) => api.post('/api/resume-styles/custom', data),
  updateCustomStyle: (id, data) => api.put(`/api/resume-styles/custom/${id}`, data),
  deleteCustomStyle: (id) => api.delete(`/api/resume-styles/custom/${id}`),

  // Delete a resume (options.params e.g. { force: true } for force delete)
  deleteResume: (resumeId, options = {}) =>
    api.delete(`/api/resumes/${resumeId}`, { params: options.params || {} }),

  // Function to rename a version
  renameVersion: (resumeId, versionId, name) =>
    api.patch(`/api/resumes/${resumeId}/versions/${versionId}/rename`, { name }),

  // Function to delete a version
  deleteVersion: (resumeId, versionId) =>
    api.delete(`/api/resumes/${resumeId}/versions/${versionId}`),

  // Function to create a new version
  createVersion: (resumeId, versionData) =>
    api.post(`/api/resumes/${resumeId}/versions`, versionData),

  getJobRecommendations: (resumeId, versionId, resumeContent = null, options = {}) =>
    api.post(`/api/resumes/${resumeId}/job-recommendations`, { versionId, resumeContent, cost: options.cost }),

  polishResumeForJobType: (resumeId, versionId, jobTypeId, recommendations, additionalInfo, resumeContent = null, followUpAnswers = null, sections = null, experienceIndexes = null, options = {}) =>
    api.post(`/api/resumes/${resumeId}/polish-for-job`, {
      versionId,
      jobTypeId,
      recommendations,
      additionalInfo,
      resumeContent,
      followUpAnswers,
      sections,
      experienceIndexes,
      cost: options.cost
    }),

  // Function to update a specific version
  updateVersion: (resumeId, versionId, updateData) =>
    api.patch(`/api/resumes/${resumeId}/versions/${versionId}`, updateData),

  // Generate a new resume based on career direction using AI (returns analysis, doesn't save)
  createResumeFromCareerDirection: (data) =>
    api.post('/api/resumeai/generate-from-career-direction', data),

  // Save the generated resume to database
  saveGeneratedResume: (data) =>
    api.post('/api/resumeai/save-generated-resume', data),

  // Clone a resume
  cloneResume: (resumeId) =>
    api.post(`/api/resumes/${resumeId}/clone`)
};

// Job API endpoints
const jobApi = {
  createJob: (jobData) => api.post('/api/jobs', jobData),

  getAllJobs: () => api.get('/api/jobs'),

  getJobById: (jobId) => api.get(`/api/jobs/${jobId}`),

  // Use PUT for full job updates
  updateJob: (jobId, jobData) => api.put(`/api/jobs/${jobId}`, jobData),

  parseJobDescription: (description) =>
    api.post('/api/jobs/parse-description', { description }),


  updateJobStatus: (jobId, statusData) =>
    api.patch(`/api/jobs/${jobId}/status`, statusData),


  // Updated to send notes as a direct string value
  deleteJob: (jobId) => api.delete(`/api/jobs/${jobId}`),
  applyForJob: (jobId, resumeId, versionId, notes = '', coverLetterId = null) =>
    api.post(`/api/jobs/${jobId}/apply`, {
      resumeId,
      versionId,
      notes,
      coverLetterId
    }),

  // Add this to your jobApi object
  updateCoverLetter: (coverLetterId, coverLetterData) => 
  api.patch(`/api/cover-letters/${coverLetterId}`, coverLetterData.content),

  checkCachedMatch: (jobId, resumeId) =>
    api.get(`/api/jobs/${jobId}/match/check`, { params: { resumeId } }),

  analyzeJobMatch: (jobId, resumeId, versionId = null, resumeContent = null, jobContent = null, options = {}) =>
    api.post(`/api/jobs/${jobId}/match`, {
      resumeId,
      versionId,
      resumeContent,
      jobContent,
      forceRefresh: options.forceRefresh || false,
      cost: options.cost
    }),

  generateCoverLetter: (jobId, resumeId, versionId, additionalInfo = null, resumeContent = null, jobContent = null, options = {}) =>
    api.post(`/api/jobs/${jobId}/generate-cover-letter`, {
      resumeId,
      versionId,
      additionalInfo,
      resumeContent,
      jobContent,
      cost: options.cost,
      wordCount: options.wordCount,
      customInstruction: options.customInstruction,
    }),

  updateJobMatchAdditionalInfo: (jobId, resumeId, additionalInfo, followUpAnswers, versionId = null) =>
    api.patch(`/api/jobs/${jobId}/match/${resumeId}/additional-info`, {
      additionalInfo,
      followUpAnswers,
      versionId
    }),

};
const professionalProfileApi = {
  listProfiles: () => api.get('/api/professional-profile'),
  createProfile: (profile) => api.post('/api/professional-profile', profile),
  getProfileById: (profileId) => api.get(`/api/professional-profile/${profileId}`),
  updateProfile: (profileId, updates) => api.patch(`/api/professional-profile/${profileId}`, updates),
  deleteProfile: (profileId) => api.delete(`/api/professional-profile/${profileId}`),
  listUniversityCourses: (university) => api.get(`/api/professional-profile/university/${encodeURIComponent(university)}/courses`),
  // New integrated upload that handles everything in backend
  uploadResumeAndIntegrateWithProfile: (file, profileData, selectedResumeId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('profile_data', JSON.stringify(profileData));
    if (selectedResumeId) {
      formData.append('selected_resume_id', selectedResumeId);
    }
    return api.post('/api/professional-profile/upload-resume-and-integrate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  // Cleaned flow: backend fetches stored resume automatically
  bootstrapAndDirectionsAuto: (profile) => api.post('/api/professional-profile/bootstrap-and-directions-auto', { profile }),
  aiDirectionsSync: (profileId) => api.post(`/api/professional-profile/${profileId}/ai/directions-sync`, {}),
  aiAnalyze: (profile, direction) => api.post('/api/professional-profile/ai/analyze', { profile, direction }),
  aiSynthesize: (profile, inputs) => api.post('/api/professional-profile/ai/synthesize', { profile, inputs }),
  syncFromResume: (profileId, resumeContent) => api.post(`/api/professional-profile/${profileId}/sync-from-resume`, { resume_content: resumeContent }),

  // NEW ENHANCED ENDPOINTS
  generateCareerDirections: (selectedResumeId = null) => {
    const payload = selectedResumeId ? { selected_resume_id: selectedResumeId } : {};
    return api.post('/api/professional-profile/ai/generate-directions', payload);
  },
  selectUniversityCourses: (profileId, university, selectedCourses) => api.post(`/api/professional-profile/${profileId}/courses/select`, { university, selected_courses: selectedCourses }),
  setPrimaryDirection: (profileId, directionId) => api.post(`/api/professional-profile/${profileId}/directions/${directionId}/set-primary`),
  submitFollowupResponses: (profileId, directionId, responses) => api.post(`/api/professional-profile/${profileId}/directions/${directionId}/followup`, { responses }),
  finalizeProfile: (profileId) => api.patch(`/api/professional-profile/${profileId}/finalize`),
  getUniversityCoursesEnhanced: (university) => api.get(`/api/professional-profile/university/${encodeURIComponent(university)}/courses/enhanced`),
  analyzeCourseRelevance: (data) => api.post('/api/professional-profile/ai/analyze-course-relevance', data),
};
// New Applications API endpoints
// Find or add this section in src/services/api.js
const applicationApi = {
  getAllApplications: () => api.get('/api/applications'),

  getApplicationById: (applicationId) => api.get(`/api/applications/${applicationId}`),

  createApplication: (applicationData) => api.post('/api/applications', applicationData),

  updateApplication: (applicationId, updateData) => api.patch(`/api/applications/${applicationId}`, updateData),

  addNote: (applicationId, note) => api.post(`/api/applications/${applicationId}/notes`, { note }),

  addInterviewNote: (applicationId, noteData) =>
    api.post(`/api/applications/${applicationId}/interview-notes`, noteData),

  deleteApplication: (applicationId) => api.delete(`/api/applications/${applicationId}`),

  getCoverLetterById: (coverLetterId) => api.get(`/api/cover-letters/${coverLetterId}`),

  getCoverLetters: () => api.get('/api/cover-letters'),

  generateCoverLetterPDF: (coverLetterId, options = {}) => {
    const body = (options.fileName != null && String(options.fileName).trim()) ? { fileName: String(options.fileName).trim() } : {};
    return api.post(`/api/cover-letters/${coverLetterId}/pdf`, body)
      .then(response => {
        if (response.data && response.data.success && response.data.fileUrl) {
          window.open(response.data.fileUrl, '_blank');
          toast.success('Cover letter PDF generated successfully. Starting download...');
          if (response.data.pageCount) {
            toast.info(`Cover letter page count: ${response.data.pageCount}`);
          }
        } else {
          toast.error('Failed to generate cover letter PDF. No download URL received.');
        }
        return response;
      })
      .catch(error => {
        console.error('Error generating cover letter PDF:', error);
        toast.error('Failed to generate cover letter PDF');
        return Promise.reject(error);
      });
  },

  // Interview Questions API endpoints
  getInterviewQuestions: (applicationId) =>
    api.get(`/api/applications/${applicationId}/interview-questions`),

  generateInterviewQuestions: (applicationId, options = {}) =>
    api.post(`/api/applications/${applicationId}/interview-questions`, { cost: options.cost }),

  saveInterviewQuestions: (applicationId, questions) =>
    api.post(`/api/applications/${applicationId}/interview-questions/save`, { questions }),

  deleteInterviewQuestion: (applicationId, questionId) =>
    api.delete(`/api/applications/${applicationId}/interview-questions/${questionId}`)
};


// Professional Profile API





// Payment API endpoints
const paymentApi = {
  getTokenPackages: () => api.get('/api/payments/packages'),

  createPaymentIntent: (packageId) =>
    api.post('/api/payments/create-payment-intent', { package_id: packageId }),

  getUserTransactions: () => api.get('/api/payments/user/transactions'),
};

// Dashboard API (stats + next-step suggestion)
const dashboardApi = {
  getStats: () => api.get('/api/dashboard/stats'),
  getNextStep: () => api.get('/api/dashboard/next-step'),
  getDiagnosesList: () => api.get('/api/dashboard/diagnoses-list'),
};

// Analytics API (Applications > Statistics dashboard)
const analyticsApi = {
  getDashboard: () => api.get('/api/analytics/dashboard'),
};

// Diagnosis API (path: /api/diagnosis - ensure correct spelling)
const DIAGNOSIS_API = '/api/diagnosis';
const diagnosisApi = {
  runQuickDiagnosis: (data) => api.post(`${DIAGNOSIS_API}/run`, data, { timeout: 120000 }),
  getLatest: () => api.get(`${DIAGNOSIS_API}/latest`),
  getForResume: (resumeId) => api.get(`${DIAGNOSIS_API}/resume/${resumeId}`),
};

const careerDirectionApi = {
  getForResume: (resumeId) => api.get(`/api/career-directions?resumeId=${resumeId}`),
  generate: (resumeId) =>
    api.post('/api/career-directions/generate', { resumeId }, { timeout: 180000 }),
};

// Promo / lead-capture diagnosis (public endpoints + claim)
const PROMO_API = '/api/promo';
const promoApi = {
  diagnose: ({ email, resumeText, file }) => {
    const formData = new FormData();
    formData.append('email', email || '');
    if (resumeText) formData.append('resume_text', resumeText);
    if (file) formData.append('file', file);
    return api.post(`${PROMO_API}/diagnose`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  getDiagnosis: (leadId) => api.get(`${PROMO_API}/diagnosis/${leadId}`),
  claim: (leadId) =>
    api.post(`${PROMO_API}/claim`, leadId ? { lead_id: leadId } : {}),
};

export { authApi, resumeApi, jobApi, applicationApi, ticketApi, mentorService, questionnaireApi, professionalProfileApi, paymentApi, dashboardApi, diagnosisApi, careerDirectionApi, analyticsApi, promoApi };
export default api;